import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, GripVertical, Eye, EyeOff, FolderTree, FlaskConical } from 'lucide-react';

import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  categoryLockedFields,
  isErp,
  type SupplierCategory,
} from '@/services/catalog';
import { PageHeader, Spinner, EmptyState } from '@/components/fields';
import { useAuthStore } from '@/store/auth.store';

export default function Categories() {
  const qc = useQueryClient();
  const devMode = useAuthStore((s) => s.supplier?.erpDeveloperMode) ?? false;
  const [newName, setNewName] = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['categories'] });

  const create = useMutation({
    mutationFn: (name: string) => createCategory({ name }),
    onSuccess: () => {
      setNewName('');
      invalidate();
      toast.success('Категория добавлена');
    },
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<SupplierCategory> }) =>
      updateCategory(id, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Категория удалена');
    },
  });

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (name.length < 2) {
      toast.error('Слишком короткое название');
      return;
    }
    create.mutate(name);
  };

  return (
    <div>
      <PageHeader title="Категории" subtitle="Группировка товаров в каталоге" />

      <form onSubmit={onAdd} className="card p-3 mb-4 flex gap-2">
        <input
          className="input"
          placeholder="Новая категория, напр. «Овощи»"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button type="submit" className="btn-primary shrink-0" disabled={create.isPending}>
          <Plus size={16} /> Добавить
        </button>
      </form>

      {isLoading ? (
        <Spinner label="Загружаем категории…" />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={<FolderTree size={40} strokeWidth={1.4} />}
          title="Пока нет категорий"
          description="Добавьте первую категорию — без неё товары не сгруппировать."
        />
      ) : (
        <div className="card divide-y divide-line">
          {data.map((cat) => (
            <CategoryRow
              key={cat.id}
              cat={cat}
              devMode={devMode}
              onRename={(name) => update.mutate({ id: cat.id, body: { name } })}
              onToggle={() => update.mutate({ id: cat.id, body: { isActive: !cat.isActive } })}
              onDelete={() => {
                // Категорию из 1С удаляем только в режиме разработчика — иначе
                // обмен вернёт её обратно.
                if (isErp(cat) && !devMode) {
                  toast.error('Категорию ведёт 1С — удалите её в учётной системе');
                  return;
                }
                const warn = isErp(cat)
                  ? `Удалить категорию «${cat.name}»? Её ведёт 1С — ближайшая выгрузка создаст её заново. Товары останутся без категории.`
                  : `Удалить категорию «${cat.name}»? Товары останутся без категории.`;
                if (confirm(warn)) remove.mutate(cat.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryRow({
  cat,
  devMode,
  onRename,
  onToggle,
  onDelete,
}: {
  cat: SupplierCategory;
  devMode: boolean;
  onRename: (name: string) => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(cat.name);
  // Название категории из 1С правится только там — следующий обмен вернёт своё.
  // Режим разработчика временно снимает эту блокировку (см. Integrations).
  const nameLocked = !devMode && categoryLockedFields(cat).includes('name');

  const commit = () => {
    const v = name.trim();
    if (v && v !== cat.name) onRename(v);
    else setName(cat.name);
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2.5">
      <GripVertical size={16} className="text-ink/20 shrink-0" />
      <input
        className="flex-1 bg-transparent text-sm text-ink focus:outline-none focus:bg-paper rounded px-2 py-1 -mx-2 disabled:text-ink/60"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        disabled={nameLocked}
        title={nameLocked ? 'Название приходит из 1С' : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      {isErp(cat) && (
        <span className="badge bg-blue-50 text-blue-700" title="Категорию ведёт 1С">
          1С
        </span>
      )}
      {isErp(cat) && devMode && (
        <span
          className="badge bg-amber-50 text-amber-700 inline-flex items-center gap-1"
          title="Режим разработчика: правка разрешена, но затрётся ближайшим обменом"
        >
          <FlaskConical size={11} /> dev
        </span>
      )}
      {!cat.isActive && <span className="badge bg-ink/10 text-ink/50">Скрыта</span>}
      <button
        onClick={onToggle}
        className="p-2 rounded-lg text-ink/40 hover:bg-paper hover:text-ink/70"
        title={cat.isActive ? 'Скрыть' : 'Показать'}
      >
        {cat.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
      </button>
      <button
        onClick={onDelete}
        className="p-2 rounded-lg text-ink/40 hover:bg-red-50 hover:text-red-600"
        title={isErp(cat) && !devMode ? 'Ведётся в 1С' : 'Удалить'}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}
